/**
 * Chrome-less fullscreen presentation of a dashboard (S13). Reuses the SAME
 * `DashboardGrid`/tile-view stack as the normal tab — no parallel rendering
 * path — with the toolbar/switcher/kebab stripped down to a thin strip
 * (name, playlist progress, fit toggle, exit). Tile-mutation handlers are
 * no-ops: presenting never changes the definition, even via a tile's own kebab
 * menu (Duplicate/Remove stay reachable there but do nothing while presenting).
 *
 * Fit to screen (UX review): the laid-out grid is uniformly scaled so the
 * composition fills the display instead of huddling in the top-left corner at
 * desktop text size — `services/presentFit.ts` owns the math and decides when
 * fitting does NOT apply (tall dashboards keep scrolling at 1:1). The toggle
 * opts back out to actual size.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { Button } from '@/components/ui/button';
import type { Dashboard } from '../types/dashboardTypes';
import { presentFit } from '../services/presentFit';
import { DashboardGrid } from './DashboardGrid';
import { DashboardPrewarmMount } from './DashboardPrewarmMount';
import { PlaylistCountdownRing } from './PlaylistCountdownRing';

const noopLayout = () => undefined;
const noopTileId = () => {};
const noopTile = () => {};

type PresentModeProps = {
  dashboard: Dashboard;
  /** Playlist's next dashboard, mounted off-screen to pre-warm its tiles. */
  onDeckDashboard: Dashboard | null;
  index: number;
  total: number;
  onExit: () => void;
};

export function PresentMode({ dashboard, onDeckDashboard, index, total, onExit }: PresentModeProps) {
  const { track } = useAnalytics();
  const [fitToScreen, setFitToScreen] = useState(true);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stage, setStage] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onExit]);

  // Measure the stage (fullscreen minus the strip). Layout effect + observer:
  // the first paint already knows the box, so the grid doesn't flash at 1:1.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () =>
      setStage((prev) =>
        prev.width === el.clientWidth && prev.height === el.clientHeight
          ? prev
          : { width: el.clientWidth, height: el.clientHeight },
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fit = useMemo(
    () =>
      fitToScreen
        ? presentFit({
            layouts: dashboard.tiles.map((t) => t.layout),
            viewportWidth: stage.width,
            viewportHeight: stage.height,
          })
        : null,
    [fitToScreen, dashboard.tiles, stage.width, stage.height],
  );

  const toggleFit = useCallback(() => {
    const enabled = !fitToScreen;
    setFitToScreen(enabled);
    track('feature:dashboard_present_fit', { enabled });
  }, [fitToScreen, track]);

  // Honest about the cases `presentFit` declines (taller than the screen, or
  // already filling it) instead of claiming a scale that isn't happening.
  const fitToScreenTitle = !fitToScreen
    ? 'Actual size — desktop scale. Click to scale the dashboard up to fill the display.'
    : fit
      ? `Fit to screen is on — scaled ${fit.scale.toFixed(1)}× to fill the display. Click for actual size.`
      : 'Fit to screen is on, but this dashboard is taller than the display (or already fills it), so it stays at actual size and scrolls.';

  const grid = (
    <DashboardGrid
      dashboardId={dashboard.id}
      tiles={dashboard.tiles}
      editMode={false}
      onLayoutCommit={noopLayout}
      onRemoveTile={noopTileId}
      onDuplicateTile={noopTile}
    />
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background" data-testid="dashboard-present-mode">
      <div className="flex items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground">
        <span className="truncate font-medium text-foreground" data-testid="present-mode-name">
          {dashboard.name}
        </span>
        {total > 1 && (
          <span className="flex items-center gap-1.5">
            <PlaylistCountdownRing key={index} />
            <span data-testid="present-mode-progress">
              {index + 1} / {total}
            </span>
          </span>
        )}
        <span className="flex-1" />
        <Button
          variant={fitToScreen ? 'secondary' : 'ghost'}
          size="sm"
          className="h-6 gap-1 text-xs"
          onClick={toggleFit}
          aria-pressed={fitToScreen}
          title={fitToScreenTitle}
          data-testid="present-mode-fit-toggle"
        >
          <Maximize2 className="h-3 w-3" />
          Fit to screen
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={onExit}
          title="Exit presentation (Esc)"
          data-testid="present-mode-exit"
        >
          <X className="h-3 w-3" />
          Esc to exit
        </Button>
      </div>
      <div
        ref={stageRef}
        className="relative flex flex-1 flex-col overflow-hidden"
        data-testid="present-mode-stage"
      >
        {fit ? (
          // Scaled composition, centered: the wrapper lays the grid out at the
          // design canvas width, the transform pulls the used bounding box to
          // the middle of the stage and magnifies it. `transform` is applied
          // right-to-left: origin shift → scale → recentre.
          <div
            className="absolute left-1/2 top-1/2"
            style={{
              width: fit.wrapperWidth,
              transformOrigin: 'top left',
              transform: `translate(${-fit.scaledWidth / 2}px, ${-fit.scaledHeight / 2}px) scale(${fit.scale}) translate(${-fit.offsetX}px, ${-fit.offsetY}px)`,
            }}
            data-testid="present-mode-fit-stage"
            data-present-scale={fit.scale.toFixed(2)}
          >
            {grid}
          </div>
        ) : (
          grid
        )}
      </div>
      {onDeckDashboard && (
        <DashboardPrewarmMount dashboardId={onDeckDashboard.id} tiles={onDeckDashboard.tiles} />
      )}
    </div>
  );
}
